import React, { useCallback, useEffect, useRef, useState } from "react";
import { updateInvoiceConfig, viewInvoiceConfig } from "../../api/InvoiceSettingsService";
import FullPageLoader from "../../common/FullPageLoader";
import ActionIconButton from "../Shared/ActionIconButton";
import { FaEdit } from "react-icons/fa";

const emptySettings = {
  companyName: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  pincode: "",
  pan: "",
  gstin: "",
  website: "",
  email: "",
  phone: "",
  fontFile: "",
  footerText: "",
};

const ImageUpload = ({ label, name, image, error, onChange, onRemove }) => {
  const inputRef = useRef(null);

  const handleRemove = () => {
    if (inputRef.current) {
      inputRef.current.value = "";
    }
    onRemove();
  };

  return (
    <div className="mb-3">
      <label className="form-label fw-semibold" htmlFor={name}>
        {label} <span className="text-danger">*</span>
      </label>

      <input
        ref={inputRef}
        id={name}
        name={name}
        type="file"
        accept="image/*"
        className="form-control"
        onChange={onChange}
      />

      {error && <small className="error">{error}</small>}

      {image && (
        <div className="mt-3 position-relative d-inline-block">
          <img
            src={image}
            alt={`${label} preview`}
            className="img-thumbnail"
            style={{
              maxWidth: "220px",
              maxHeight: "120px",
              objectFit: "contain",
            }}
          />

          <button
            type="button"
            className="position-absolute top-0 end-0 m-1"
            style={{
              width: "1.5rem",
              height: "1.5rem",
              padding: 0,
              border: 0,
              borderRadius: "50%",
              backgroundColor: "#212529",
              color: "#dc3545",
              fontSize: "1.25rem",
              lineHeight: 1,
            }}
            aria-label={`Remove ${label.toLowerCase()} image`}
            onClick={handleRemove}
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
};

const InvoiceSettings = () => {
  const [configId, setConfigId] = useState("");
  const [formData, setFormData] = useState(emptySettings);
  const [logo, setLogo] = useState("");
  const [sign, setSign] = useState("");
  const [fontFile, setFontFile] = useState("");
  const [fontFileKey, setFontFileKey] = useState("");
  const [fontFileName, setFontFileName] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [signUrl, setSignUrl] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);

  const [errors, setErrors] = useState({});
  const [saveMessage, setSaveMessage] = useState("");
  const [saveStatus, setSaveStatus] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadConfig = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await viewInvoiceConfig();
      const config =
        response?.data?.config ||
        response?.data?.data ||
        response?.data ||
        response?.config ||
        response;
      const record = Array.isArray(config) ? config[0] : config;
      if (!record || typeof record !== "object") {
        throw new Error("Invoice configuration was not found.");
      }
      setConfigId(record.configId || record._id || record.id || "");
      setFormData({
        ...emptySettings,
        ...record,
        email: record.email || record.companyEmail || "",
        phone: record.phone || record.companyPhone || "",
        pan: record.pan || record.panNumber || "",
      });
      setLogo(record.logoImage || "");
      setSign(record.signatureImage || "");
      setFontFile("");
      setFontFileKey(record.fontFileKey || "");
      setFontFileName("");
      setLogoUrl(record.logoImageUrl || "");
      setSignUrl(record.signatureImageUrl || "");
    } catch (error) {
      setSaveMessage(
        error.response?.data?.message || error.message || "Could not load invoice settings.",
      );
      setSaveStatus("error");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((current) => ({
      ...current,
      [name]: value,
    }));

    setErrors((current) => ({
      ...current,
      [name]: "",
    }));

    setSaveMessage("");
    setSaveStatus("");
  };

  const readImage = (file, setter, fieldName) => {
    if (!file) return;

    const reader = new FileReader();

    reader.onload = () => {
      setter(reader.result);

      setErrors((current) => ({
        ...current,
        [fieldName]: "",
      }));

      setSaveMessage("");
      setSaveStatus("");
    };

    reader.onerror = () => {
      setSaveMessage("Could not read the selected image.");
      setSaveStatus("error");
    };

    reader.readAsDataURL(file);
  };

  const readFontFile = (file) => {
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      setFontFile(reader.result);
      setFontFileName(file.name);
      setErrors((current) => ({ ...current, fontFile: "" }));
      setSaveMessage("");
      setSaveStatus("");
    };
    reader.onerror = () => {
      setSaveMessage("Could not read the selected font file.");
      setSaveStatus("error");
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (isSubmitting) return;

    setIsSubmitting(true);
    setErrors({});
    setSaveMessage("");
    setSaveStatus("");

    try {
      const payload = {
        configId,
        companyName: formData.companyName,
        addressLine1: formData.addressLine1,
        addressLine2: formData.addressLine2,
        city: formData.city,
        state: formData.state,
        pincode: formData.pincode,
        pan: formData.pan,
        gstin: formData.gstin,
        website: formData.website,
        email: formData.email,
        phone: formData.phone,
        footerText: formData.footerText,
        logoImage: logo,
        signatureImage: sign,
      };
      if (fontFile) payload.fontFile = fontFile;

      const response = await updateInvoiceConfig(payload);
      if (response?.success === false || response?.status >= 400) {
        const validationErrors = response.error || response.errors;
        if (
          validationErrors &&
          typeof validationErrors === "object" &&
          !Array.isArray(validationErrors)
        ) {
          setErrors(validationErrors);
        }
        setSaveMessage(response.message);
        setSaveStatus("error");
        return;
      }

      setErrors(response?.error || {});
      setSaveStatus("success");
      setSaveMessage(response?.message);
      await loadConfig();
      setIsEditing(false);
    } catch (error) {
      const responseData = error.response?.data || error.data;
      const validationErrors = responseData?.error || responseData?.errors || error.error;

      if (
        (error.response?.status === 422 || responseData?.status === 422) &&
        validationErrors &&
        typeof validationErrors === "object" &&
        !Array.isArray(validationErrors)
      ) {
        setErrors(validationErrors);
        setSaveMessage(responseData?.message || "Validation failed");
        setSaveStatus("error");
      } else {
        setSaveMessage(
          responseData?.message ||
            (typeof responseData === "string" ? responseData : "") ||
            error.message,
        );
        setSaveStatus("error");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <FullPageLoader text="Loading invoice settings..." />;
  }

  return (
    <div className="container-fluid p-4">
      {isSubmitting && <FullPageLoader text="Updating invoice settings..." />}
      <div className="card p-4">
        {!isEditing ? (
          <>
            <div className="d-flex justify-content-end align-items-center mb-4">
              <ActionIconButton
                icon={<FaEdit />}
                label="Edit"
                variant="edit"
                onClick={() => setIsEditing(true)}
              />
            </div>
            <div className="row">
              {[
                ["Company name", formData.companyName],
                ["Address line 1", formData.addressLine1],
                ["Address line 2", formData.addressLine2],
                ["City", formData.city],
                ["State", formData.state],
                ["Pincode", formData.pincode],
                ["PAN", formData.pan],
                ["GSTIN", formData.gstin],
                ["Website", formData.website],
                ["Email", formData.email],
                ["Phone", formData.phone],
                ["Font file", fontFileKey ? fontFileKey.split("/").pop() : "—"],
                ["Footer text", formData.footerText],
              ].map(([label, value]) => (
                <div className="col-md-6 mb-3" key={label}>
                  <div className="text-muted small">{label}</div>
                  <div>{value || "—"}</div>
                </div>
              ))}
              {(logoUrl || logo) && (
                <div className="col-6 ">
                  <div className="text-muted small">Logo</div>
                  <img
                    src={logoUrl || logo}
                    alt="Invoice logo"
                    style={{ maxWidth: 220, maxHeight: 120, objectFit: "contain" }}
                  />
                </div>
              )}
              {(signUrl || sign) && (
                <div className="col-6">
                  <div className="text-muted small">Signature</div>
                  <img
                    src={signUrl || sign}
                    alt="Invoice signature"
                    style={{ maxWidth: 220, maxHeight: 120, objectFit: "contain" }}
                  />
                </div>
              )}
            </div>
          </>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            <div className="row">
              <div className="col-md-12 mb-3">
                <label className="form-label fw-semibold" htmlFor="companyName">
                  Company name <span className="text-danger">*</span>
                </label>
                <input
                  id="companyName"
                  name="companyName"
                  type="text"
                  className="form-control"
                  value={formData.companyName}
                  onChange={handleChange}
                />
                {errors.companyName && <small className="error">{errors.companyName}</small>}
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label fw-semibold" htmlFor="email">
                  Company email <span className="text-danger">*</span>
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  className="form-control"
                  value={formData.email}
                  onChange={handleChange}
                />
                {errors.email && <small className="error">{errors.email}</small>}
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label fw-semibold" htmlFor="phone">
                  Company phone <span className="text-danger">*</span>
                </label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  className="form-control"
                  value={formData.phone}
                  onChange={handleChange}
                />
                {errors.phone && <small className="error">{errors.phone}</small>}
              </div>

              <div className="col-md-4 mb-3">
                <label className="form-label fw-semibold" htmlFor="pan">
                  PAN no <span className="text-danger">*</span>
                </label>
                <input
                  id="pan"
                  name="pan"
                  type="text"
                  className="form-control"
                  value={formData.pan}
                  onChange={handleChange}
                />
                {errors.pan && <small className="error">{errors.pan}</small>}
              </div>

              <div className="col-md-4 mb-3">
                <label className="form-label fw-semibold" htmlFor="gstin">
                  GSTIN <span className="text-danger">*</span>
                </label>
                <input
                  id="gstin"
                  name="gstin"
                  type="text"
                  className="form-control"
                  value={formData.gstin}
                  onChange={handleChange}
                />
                {errors.gstin && <small className="error">{errors.gstin}</small>}
              </div>

              <div className="col-md-4 mb-3">
                <label className="form-label fw-semibold" htmlFor="pincode">
                  Pincode <span className="text-danger">*</span>
                </label>
                <input
                  id="pincode"
                  name="pincode"
                  type="text"
                  className="form-control"
                  value={formData.pincode}
                  onChange={handleChange}
                />
                {errors.pincode && <small className="error">{errors.pincode}</small>}
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label fw-semibold" htmlFor="website">
                  Website <span className="text-danger">*</span>
                </label>
                <input
                  id="website"
                  name="website"
                  type="text"
                  className="form-control"
                  value={formData.website}
                  onChange={handleChange}
                />
                {errors.website && <small className="error">{errors.website}</small>}
              </div>

              <div className="col-md-3 mb-3">
                <label className="form-label fw-semibold" htmlFor="state">
                  State <span className="text-danger">*</span>
                </label>
                <input
                  id="state"
                  name="state"
                  type="text"
                  className="form-control"
                  value={formData.state}
                  onChange={handleChange}
                />
                {errors.state && <small className="error">{errors.state}</small>}
              </div>

              <div className="col-md-3 mb-3">
                <label className="form-label fw-semibold" htmlFor="city">
                  City <span className="text-danger">*</span>
                </label>
                <input
                  id="city"
                  name="city"
                  type="text"
                  className="form-control"
                  value={formData.city}
                  onChange={handleChange}
                />
                {errors.city && <small className="error">{errors.city}</small>}
              </div>

              <div className="col-md-12 mb-3">
                <label className="form-label fw-semibold" htmlFor="addressLine1">
                  Address line 1 <span className="text-danger">*</span>
                </label>
                <input
                  id="addressLine1"
                  name="addressLine1"
                  type="text"
                  className="form-control"
                  value={formData.addressLine1}
                  onChange={handleChange}
                />
                {errors.addressLine1 && <small className="error">{errors.addressLine1}</small>}
              </div>

              <div className="col-md-12 mb-3">
                <label className="form-label fw-semibold" htmlFor="addressLine2">
                  Address line 2 <span className="text-danger">*</span>
                </label>
                <input
                  id="addressLine2"
                  name="addressLine2"
                  type="text"
                  className="form-control"
                  value={formData.addressLine2}
                  onChange={handleChange}
                />
                {errors.addressLine2 && <small className="error">{errors.addressLine2}</small>}
              </div>

              <div className="col-md-6">
                <ImageUpload
                  label="Logo"
                  name="invoice-logo"
                  image={logo || logoUrl}
                  error={errors.logoImage}
                  onChange={(event) => readImage(event.target.files?.[0], setLogo, "logoImage")}
                  onRemove={() => {
                    setLogo("");
                    setErrors((current) => ({
                      ...current,
                      logoImage: "",
                    }));
                  }}
                />
              </div>

              <div className="col-md-6">
                <ImageUpload
                  label="Sign"
                  name="invoice-sign"
                  image={sign || signUrl}
                  error={errors.signatureImage}
                  onChange={(event) =>
                    readImage(event.target.files?.[0], setSign, "signatureImage")
                  }
                  onRemove={() => {
                    setSign("");
                    setErrors((current) => ({ ...current, signatureImage: "" }));
                  }}
                />
              </div>

              <div className="col-md-12 mb-3">
                <label className="form-label fw-semibold" htmlFor="invoice-font-file">
                  Font file
                </label>
                <input
                  id="invoice-font-file"
                  name="fontFile"
                  type="file"
                  accept=".ttf,.otf,.woff,.woff2,font/ttf,font/otf,font/woff,font/woff2"
                  className="form-control"
                  onChange={(event) => readFontFile(event.target.files?.[0])}
                />
                <small className="text-muted">
                  {fontFileName ||
                    (fontFileKey
                      ? `Current font: ${fontFileKey.split("/").pop()}`
                      : "Choose a .ttf, .otf, .woff, or .woff2 file.")}
                </small>
                {errors.fontFile && <small className="error d-block">{errors.fontFile}</small>}
              </div>

              <div className="col-md-12 mb-3">
                <label className="form-label fw-semibold" htmlFor="footerText">
                  Footer text <span className="text-danger">*</span>
                </label>
                <textarea
                  id="footerText"
                  name="footerText"
                  className="form-control"
                  rows="3"
                  value={formData.footerText}
                  onChange={handleChange}
                />
                {errors.footerText && <small className="error">{errors.footerText}</small>}
              </div>
            </div>

            <div className="d-flex justify-content-end gap-2">
              <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                {isSubmitting ? "Updating..." : "Update"}
              </button>
              <button
                type="button"
                className="btn"
                style={{
                  backgroundColor: "#032B4D",
                  color: "#fff",
                }}
                onClick={async () => {
                  await loadConfig();
                  setIsEditing(false);
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default InvoiceSettings;
